/**
 * Does a file carry a whole saved scene (contract v65, `of-scene/2`)? Read on
 * the HOST, before the file reaches the viewer, so the studio knows up front
 * whether a model load arrives with its own look — the "Apply default
 * materials" dialog must not ask about a piece whose file already says what
 * every part is made of, and that has to be decided before the model appears
 * (see `state/editor/modelLoads.ts`).
 *
 * Two shapes carry the document:
 *
 *   - a GLB: a 12-byte header, then a JSON chunk whose root
 *     `extensions.OF_scene` is the document. A `.jscene` is an empty glTF
 *     scene with the block; a "Ring + scene" `.glb` is the ring with it. Only
 *     the header and the JSON chunk are read — never the BIN chunk, which holds
 *     every packed file (environments, textures…) and can be tens of MB;
 *   - JSON text: the document itself (`exportSceneJSON`, a Vault save).
 *
 * Pure apart from `Blob.slice`, and every read is bounded: a file is untrusted.
 */
import { OF_SCENE_EXTENSION, OF_SCENE_SCHEMA, type SceneFileKind } from "../shared/api-contract";

const GLB_MAGIC = 0x46546c67; // "glTF"
const CHUNK_JSON = 0x4e4f534a; // "JSON"
const HEADER_BYTES = 12;
const CHUNK_HEADER_BYTES = 8;
/** A JSON chunk bigger than this is not a scene header worth reading. */
export const MAX_SNIFF_JSON_BYTES = 64 * 1024 * 1024;

export interface SceneSniff {
  kind: SceneFileKind;
  /** The schema the block names ("of-scene/2"). */
  schema: string;
  /** The glTF has meshes — a ring to load, not only settings. */
  hasModel: boolean;
}

/** True when `head` starts with the GLB magic. */
export function isGlbHead(head: Uint8Array): boolean {
  return head.byteLength >= 4 && new DataView(head.buffer, head.byteOffset, 4).getUint32(0, true) === GLB_MAGIC;
}

/**
 * Bytes from the start of a GLB that cover its header and whole JSON chunk —
 * what has to be read before the JSON can be parsed. Null for anything that is
 * not a GLB with a JSON first chunk (needs 20 bytes to say).
 */
export function glbJsonPrefixLength(head: Uint8Array): number | null {
  if (head.byteLength < HEADER_BYTES + CHUNK_HEADER_BYTES || !isGlbHead(head)) return null;
  const dv = new DataView(head.buffer, head.byteOffset, head.byteLength);
  if (dv.getUint32(16, true) !== CHUNK_JSON) return null;
  return HEADER_BYTES + CHUNK_HEADER_BYTES + dv.getUint32(12, true);
}

const isRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);

/** The scene block of a parsed glTF JSON, or null. */
function sniffGltfJson(json: unknown): SceneSniff | null {
  if (!isRecord(json)) return null;
  const doc = isRecord(json.extensions) ? json.extensions[OF_SCENE_EXTENSION] : undefined;
  if (!isRecord(doc)) return null;
  return {
    kind: doc.kind === "ring-scene" ? "ring-scene" : "scene",
    schema: typeof doc.schema === "string" ? doc.schema : "",
    hasModel: Array.isArray(json.meshes) && json.meshes.length > 0,
  };
}

/**
 * The scene a GLB carries, from its first bytes. `head` must hold at least
 * `glbJsonPrefixLength(head)` bytes; a shorter head, a non-GLB, a JSON chunk
 * that does not parse or a GLB without the block all answer null.
 */
export function sniffGlbHead(head: Uint8Array): SceneSniff | null {
  const need = glbJsonPrefixLength(head);
  if (need == null || head.byteLength < need) return null;
  try {
    return sniffGltfJson(JSON.parse(new TextDecoder().decode(head.subarray(HEADER_BYTES + CHUNK_HEADER_BYTES, need))));
  } catch {
    return null;
  }
}

/** The of-scene/2 document a JSON text is, or null (an older `.vjson`, junk). */
export function sniffSceneJsonText(text: string): SceneSniff | null {
  if (!text.trimStart().startsWith("{")) return null;
  try {
    const doc = JSON.parse(text) as unknown;
    if (!isRecord(doc) || doc.schema !== OF_SCENE_SCHEMA) return null;
    return { kind: doc.kind === "ring-scene" ? "ring-scene" : "scene", schema: OF_SCENE_SCHEMA, hasModel: false };
  } catch {
    return null;
  }
}

/**
 * The scene a file carries, reading as little of it as possible: a GLB's
 * header and JSON chunk, or — for a `.json` / `.vjson` / `.jscene` that is
 * not a GLB — the whole text. Never rejects; anything unreadable is null.
 */
export async function sniffSceneFile(file: Blob): Promise<SceneSniff | null> {
  try {
    const first = new Uint8Array(await file.slice(0, HEADER_BYTES + CHUNK_HEADER_BYTES).arrayBuffer());
    if (isGlbHead(first)) {
      const need = glbJsonPrefixLength(first);
      if (need == null || need > file.size || need > MAX_SNIFF_JSON_BYTES) return null;
      return sniffGlbHead(new Uint8Array(await file.slice(0, need).arrayBuffer()));
    }
    if (file.size > MAX_SNIFF_JSON_BYTES) return null;
    return sniffSceneJsonText(await file.text());
  } catch {
    return null;
  }
}

/** File types the scene-file pickers accept. */
export const SCENE_FILE_ACCEPT = ".jscene,.glb,.json,.vjson";

/** A `.jscene` by name — the one extension that can only be a scene file. */
export const isJsceneName = (name: string) => /\.jscene$/i.test(name);
